import { useState, useMemo } from 'react';

import { useRevenueStore } from '@/hooks/useSupabaseStore';
import { useExportRevenue } from '@/hooks/useExportRevenue';

function formatMoney(n: number) {
  return n.toLocaleString('vi-VN') + 'đ';
}

function formatDate(dateStr: string) {
  const d = new Date(dateStr);
  return d.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function formatTime(dateStr: string) {
  const d = new Date(dateStr);
  return d.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' });
}

function getDateKey(dateStr: string) {
  return dateStr.slice(0, 10);
}

const typeLabel: Record<string, { label: string; color: string; icon: string }> = {
  extend: { label: 'Đóng lãi', color: 'bg-teal-100 text-teal-700', icon: 'ri-money-dollar-circle-line' },
  redeem: { label: 'Chuộc đồ', color: 'bg-emerald-100 text-emerald-700', icon: 'ri-checkbox-circle-line' },
  other: { label: 'Khác', color: 'bg-gray-100 text-gray-600', icon: 'ri-more-line' },
};

export default function DailyRevenuePage() {
  const { logs, loading } = useRevenueStore();
  const { exportRevenue, exporting, buildMonthlyData } = useExportRevenue();

  const [selectedDate, setSelectedDate] = useState<string>('');
  const [filterType, setFilterType] = useState<string>('all');

  // Tính doanh thu hôm nay
  const todayKey = new Date().toISOString().slice(0, 10);
  const todayRevenue = useMemo(
    () => logs.filter(l => getDateKey(l.createdAt) === todayKey).reduce((s, l) => s + l.interestAmount, 0),
    [logs, todayKey]
  );

  // Tính doanh thu tháng này
  const thisMonth = new Date().toISOString().slice(0, 7);
  const monthRevenue = useMemo(
    () => logs.filter(l => l.createdAt.startsWith(thisMonth)).reduce((s, l) => s + l.interestAmount, 0),
    [logs, thisMonth]
  );

  // Nhóm logs theo ngày
  const groupedByDay = useMemo(() => {
    const filtered = logs.filter(l => {
      const matchType = filterType === 'all' || l.type === filterType;
      const matchDate = !selectedDate || getDateKey(l.createdAt) === selectedDate;
      return matchType && matchDate;
    });

    const map: Record<string, typeof logs> = {};
    filtered.forEach(l => {
      const key = getDateKey(l.createdAt);
      if (!map[key]) map[key] = [];
      map[key].push(l);
    });

    return Object.entries(map).sort((a, b) => b[0].localeCompare(a[0]));
  }, [logs, filterType, selectedDate]);

  // Tổng doanh thu đang hiển thị
  const totalShown = useMemo(
    () => groupedByDay.reduce((s, [, items]) => s + items.reduce((ss, l) => ss + l.interestAmount, 0), 0),
    [groupedByDay]
  );

  // Báo cáo tổng hợp theo tháng
  const monthlyData = useMemo(() => buildMonthlyData(logs), [logs, buildMonthlyData]);

  // Danh sách ngày có dữ liệu (cho date picker highlight)
  const availableDates = useMemo(() => new Set(logs.map(l => getDateKey(l.createdAt))), [logs]);

  return (<>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <div className="bg-white rounded-xl p-4">
          <p className="text-gray-400 text-xs mb-1">Hôm nay</p>
          <p className="font-bold text-xl text-teal-600">{formatMoney(todayRevenue)}</p>
          <p className="text-xs text-gray-400 mt-0.5">{logs.filter(l => getDateKey(l.createdAt) === todayKey).length} giao dịch</p>
        </div>
        <div className="bg-white rounded-xl p-4">
          <p className="text-gray-400 text-xs mb-1">Tháng này</p>
          <p className="font-bold text-xl text-amber-600">{formatMoney(monthRevenue)}</p>
          <p className="text-xs text-gray-400 mt-0.5">{logs.filter(l => l.createdAt.startsWith(thisMonth)).length} giao dịch</p>
        </div>
        <div className="bg-white rounded-xl p-4">
          <p className="text-gray-400 text-xs mb-1">Tổng giao dịch</p>
          <p className="font-bold text-xl text-gray-800">{logs.length}</p>
          <p className="text-xs text-gray-400 mt-0.5">Tất cả thời gian</p>
        </div>
        <div className="bg-white rounded-xl p-4">
          <p className="text-gray-400 text-xs mb-1">Đang hiển thị</p>
          <p className="font-bold text-xl text-gray-800">{formatMoney(totalShown)}</p>
          <p className="text-xs text-gray-400 mt-0.5">{groupedByDay.reduce((s, [, i]) => s + i.length, 0)} giao dịch</p>
        </div>
      </div>

      {/* Báo cáo tổng hợp theo tháng */}
      {monthlyData.length > 0 && (
        <div className="bg-white rounded-2xl overflow-hidden mb-4">
          <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-b border-gray-100 bg-gray-50/60">
            <div className="flex items-center gap-2.5">
              <span className="w-8 h-8 flex items-center justify-center bg-amber-100 rounded-lg">
                <i className="ri-bar-chart-grouped-line text-amber-600 text-base"></i>
              </span>
              <div>
                <p className="text-sm font-bold text-gray-800">Báo cáo tổng hợp theo tháng</p>
                <p className="text-xs text-gray-400">{monthlyData.length} tháng có dữ liệu</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => exportRevenue(logs, 'excel')}
                disabled={exporting}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 text-gray-700 rounded-lg text-xs font-semibold hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
              >
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-file-excel-2-line text-emerald-600 text-sm"></i></span>
                Excel toàn bộ
              </button>
              <button
                onClick={() => exportRevenue(logs, 'pdf')}
                disabled={exporting}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 text-gray-700 rounded-lg text-xs font-semibold hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
              >
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-file-pdf-line text-red-500 text-sm"></i></span>
                PDF toàn bộ
              </button>
            </div>
          </div>

          {/* Mobile card list */}
          <div className="block md:hidden divide-y divide-gray-50">
            {monthlyData.map(m => (
              <div key={m.monthKey} className="px-4 py-3.5">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <span className="text-sm font-bold text-gray-800">{m.monthName}</span>
                  <span className="text-xs font-bold text-teal-600">{formatMoney(m.total)}</span>
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 mb-2">
                  <div>
                    <p className="text-xs text-gray-400">Đóng lãi</p>
                    <p className="text-xs font-medium text-teal-700">{formatMoney(m.extend)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">Chuộc đồ</p>
                    <p className="text-xs font-medium text-emerald-700">{formatMoney(m.redeem)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">Số giao dịch</p>
                    <p className="text-xs text-gray-600">{m.count}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">Số ngày có GD</p>
                    <p className="text-xs text-gray-600">{m.dayCount}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => exportRevenue(logs, 'excel', m.monthKey)}
                    disabled={exporting}
                    className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg bg-gray-50 hover:bg-gray-100 text-gray-600 text-xs font-medium transition-colors cursor-pointer disabled:opacity-50"
                  >
                    <i className="ri-file-excel-2-line text-emerald-600 text-sm"></i> Excel
                  </button>
                  <button
                    onClick={() => exportRevenue(logs, 'pdf', m.monthKey)}
                    disabled={exporting}
                    className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg bg-gray-50 hover:bg-gray-100 text-gray-600 text-xs font-medium transition-colors cursor-pointer disabled:opacity-50"
                  >
                    <i className="ri-file-pdf-line text-red-500 text-sm"></i> PDF
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50">
                  {['Tháng', 'Tổng doanh thu', 'Đóng lãi', 'Chuộc đồ', 'Khác', 'Số GD', 'Số ngày', 'TB / ngày', ''].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {monthlyData.map(m => (
                  <tr key={m.monthKey} className="hover:bg-gray-50/60 transition-colors">
                    <td className="px-4 py-3.5 text-sm font-bold text-gray-800 whitespace-nowrap">{m.monthName}</td>
                    <td className="px-4 py-3.5 text-sm font-bold text-teal-600 whitespace-nowrap">{formatMoney(m.total)}</td>
                    <td className="px-4 py-3.5 text-sm text-teal-700 whitespace-nowrap">{formatMoney(m.extend)}</td>
                    <td className="px-4 py-3.5 text-sm text-emerald-700 whitespace-nowrap">{formatMoney(m.redeem)}</td>
                    <td className="px-4 py-3.5 text-sm text-gray-600 whitespace-nowrap">{formatMoney(m.other)}</td>
                    <td className="px-4 py-3.5 text-sm text-gray-600 whitespace-nowrap">{m.count}</td>
                    <td className="px-4 py-3.5 text-sm text-gray-600 whitespace-nowrap">{m.dayCount}</td>
                    <td className="px-4 py-3.5 text-sm text-amber-600 font-semibold whitespace-nowrap">{formatMoney(Math.round(m.total / Math.max(1, m.dayCount)))}</td>
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => exportRevenue(logs, 'excel', m.monthKey)}
                          disabled={exporting}
                          className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors cursor-pointer text-emerald-600 disabled:opacity-50"
                          title={`Xuất Excel ${m.monthName}`}
                        >
                          <i className="ri-file-excel-2-line text-sm"></i>
                        </button>
                        <button
                          onClick={() => exportRevenue(logs, 'pdf', m.monthKey)}
                          disabled={exporting}
                          className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors cursor-pointer text-red-500 disabled:opacity-50"
                          title={`Xuất PDF ${m.monthName}`}
                        >
                          <i className="ri-file-pdf-line text-sm"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Filter bar */}
      <div className="bg-white rounded-2xl px-4 py-3 mb-4 flex flex-wrap items-center gap-3">
        {/* Lọc theo loại */}
        <div className="flex items-center gap-1.5">
          {[
            { value: 'all', label: 'Tất cả' },
            { value: 'extend', label: 'Đóng lãi' },
            { value: 'redeem', label: 'Chuộc đồ' },
          ].map(opt => (
            <button
              key={opt.value}
              onClick={() => setFilterType(opt.value)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                filterType === opt.value ? 'bg-[#f59e0b] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2 ml-auto">
          {/* Chọn ngày */}
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400 pointer-events-none">
              <i className="ri-calendar-line text-sm"></i>
            </span>
            <input
              type="date"
              value={selectedDate}
              onChange={e => setSelectedDate(e.target.value)}
              className="pl-9 pr-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#f59e0b] text-gray-700 cursor-pointer"
            />
          </div>
          {selectedDate && (
            <button
              onClick={() => setSelectedDate('')}
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-500 text-xs font-medium transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-close-line text-sm"></i>
              Xóa lọc
            </button>
          )}
        </div>
      </div>

      {/* Danh sách theo ngày */}
      {loading ? (
        <div className="bg-white rounded-2xl p-12 text-center text-gray-400">
          <i className="ri-loader-4-line text-3xl animate-spin block mb-2"></i>
          <p className="text-sm">Đang tải dữ liệu...</p>
        </div>
      ) : groupedByDay.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 text-center text-gray-400">
          <span className="w-12 h-12 flex items-center justify-center mx-auto mb-3">
            <i className="ri-money-dollar-circle-line text-4xl"></i>
          </span>
          <p className="text-sm font-medium">Chưa có doanh thu nào</p>
          <p className="text-xs mt-1">Doanh thu sẽ xuất hiện khi khách hàng đóng lãi hoặc chuộc đồ</p>
        </div>
      ) : (
        <div className="space-y-4">
          {groupedByDay.map(([dateKey, items]) => {
            const dayTotal = items.reduce((s, l) => s + l.interestAmount, 0);
            const isToday = dateKey === todayKey;
            return (
              <div key={dateKey} className="bg-white rounded-2xl overflow-hidden">
                {/* Header ngày */}
                <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 bg-gray-50/60">
                  <div className="flex items-center gap-2.5">
                    <span className="w-7 h-7 flex items-center justify-center bg-amber-100 rounded-lg">
                      <i className="ri-calendar-check-line text-amber-600 text-sm"></i>
                    </span>
                    <div>
                      <span className="text-sm font-bold text-gray-800">
                        {formatDate(dateKey + 'T00:00:00')}
                      </span>
                      {isToday && (
                        <span className="ml-2 text-[10px] font-semibold bg-teal-100 text-teal-700 px-2 py-0.5 rounded-full">Hôm nay</span>
                      )}
                    </div>
                    <span className="text-xs text-gray-400">{items.length} giao dịch</span>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold text-teal-600">{formatMoney(dayTotal)}</p>
                    <p className="text-[10px] text-gray-400">Tổng ngày</p>
                  </div>
                </div>

                {/* Danh sách giao dịch trong ngày */}
                <div className="divide-y divide-gray-50">
                  {items.map(log => {
                    const t = typeLabel[log.type] || typeLabel.other;
                    return (
                      <div key={log.id} className="flex items-center gap-4 px-5 py-3.5 hover:bg-gray-50/50 transition-colors">
                        {/* Icon */}
                        <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-gray-100 shrink-0">
                          <i className={`${t.icon} text-gray-500 text-base`}></i>
                        </div>

                        {/* Thông tin */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <p className="text-sm font-semibold text-gray-800 truncate">{log.customerName}</p>
                            <span className={`shrink-0 text-[10px] font-semibold px-2 py-0.5 rounded-full ${t.color}`}>
                              {t.label}
                            </span>
                          </div>
                          <p className="text-xs text-gray-400 truncate">{log.assetName}</p>
                          {log.note && (
                            <p className="text-xs text-gray-400 truncate mt-0.5 italic">{log.note}</p>
                          )}
                        </div>

                        {/* Giờ + Số tiền */}
                        <div className="text-right shrink-0">
                          <p className="text-sm font-bold text-teal-600">{formatMoney(log.interestAmount)}</p>
                          <p className="text-[10px] text-gray-400 mt-0.5">{formatTime(log.createdAt)}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Thống kê ngày có dữ liệu */}
      {availableDates.size > 0 && !selectedDate && (
        <div className="mt-4 bg-white rounded-2xl p-5">
          <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-3">Chọn nhanh theo ngày</p>
          <div className="flex flex-wrap gap-2">
            {Array.from(availableDates).sort((a, b) => b.localeCompare(a)).slice(0, 14).map(d => {
              const dayLogs = logs.filter(l => getDateKey(l.createdAt) === d);
              const dayTotal = dayLogs.reduce((s, l) => s + l.interestAmount, 0);
              return (
                <button
                  key={d}
                  onClick={() => setSelectedDate(d)}
                  className="flex flex-col items-center px-3 py-2 rounded-xl border border-gray-200 hover:border-amber-300 hover:bg-amber-50 transition-colors cursor-pointer min-w-[72px]"
                >
                  <span className="text-xs font-bold text-gray-700">{formatDate(d + 'T00:00:00').slice(0, 5)}</span>
                  <span className="text-[10px] text-teal-600 font-semibold mt-0.5">{(dayTotal / 1000).toFixed(0)}k</span>
                  <span className="text-[9px] text-gray-400">{dayLogs.length} GD</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
}
